import { Fragment, useState } from "react";
import {
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
  Combobox as HeadlessCombobox,
} from "@headlessui/react";
import {
  CheckIcon,
  ChevronDownIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { tw } from "@/utils";
import { Spinner } from "./Spinner";

interface ComboboxProps {
  options: string[];
  value: string[] | string;
  onChange: (value: string[] | string) => void;
  hasError: boolean;
  placeholder: string;
  hasMultipleSelection?: boolean;
  isLoading?: boolean;
}

export const Combobox = ({
  options,
  value = [],
  onChange,
  hasError,
  placeholder,
  hasMultipleSelection = false,
  isLoading = false,
}: ComboboxProps) => {
  const [query, setQuery] = useState("");

  const filteredOptions =
    query === ""
      ? options
      : options.filter((option) => {
          return option.toLowerCase().includes(query.toLowerCase());
        });

  const removeOption = (optionToRemove: string) => {
    if (Array.isArray(value)) {
      onChange(value.filter((val) => val !== optionToRemove));
    }
  };

  return (
    <div>
      <HeadlessCombobox
        value={value}
        onChange={onChange}
        onClose={() => setQuery("")}
        multiple={hasMultipleSelection}
      >
        <div className="relative">
          <ComboboxInput
            placeholder={placeholder}
            className={tw(
              "h-12 w-full rounded-lg border border-neutral-200 px-4 py-3 shadow-sm",
              "hover:border-primary-200",
              "focus:border-primary-300 focus:shadow-focus focus:outline-none",
              hasError && "border-error-500",
            )}
            onChange={(event) => setQuery(event.target.value)}
          />
          <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
            <ChevronDownIcon className="size-4 group-data-[hover]:fill-white" />
          </ComboboxButton>
        </div>
        {hasMultipleSelection && value.length > 0 && Array.isArray(value) && (
          <div className="flex flex-wrap gap-2 pt-4">
            {value.map((selectedOption) => (
              <div
                key={selectedOption}
                className="flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-800"
              >
                {selectedOption}
                <button
                  type="button"
                  onClick={() => removeOption(selectedOption)}
                  className="text-primary-600 hover:text-primary-900"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        )}

        <ComboboxOptions
          anchor="bottom"
          transition
          className={tw(
            "flex w-[var(--input-width)] flex-col gap-2 rounded-md bg-white px-2 shadow-md [--anchor-gap:var(--spacing-1)] empty:invisible",
            "transition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
          )}
        >
          {isLoading ? (
            <div className="flex justify-center p-2">
              <Spinner />
            </div>
          ) : (
            filteredOptions.map((option) => {
              const isSelected = value?.includes(option);
              return (
                <ComboboxOption
                  key={option}
                  value={option}
                  className={({ active }) =>
                    tw(
                      "flex cursor-default select-none items-center rounded-md py-2 pl-3 pr-8 text-sm text-primary-950 outline-none",
                      active ? "bg-primary-50" : "",
                      isSelected
                        ? "border data-[selected]:border-primary-500 data-[selected]:bg-primary-100"
                        : "",
                    )
                  }
                >
                  {({ selected }) => (
                    <Fragment>
                      <span className="flex-1">{option}</span>
                      {selected && (
                        <CheckIcon
                          className="h-5 w-5 text-primary-600"
                          aria-hidden="true"
                        />
                      )}
                    </Fragment>
                  )}
                </ComboboxOption>
              );
            })
          )}
        </ComboboxOptions>
      </HeadlessCombobox>
    </div>
  );
};
